<template>
  <div
    v-if="traits && !isOutOfStock"
    class="color-and-size-container"
  >
    <div
      v-if="selectedColorName"
      class="color-name-label"
    >
      <span class="field-name">COLOR:</span>  {{ selectedColorName }}
    </div>
    <div v-if="colorMap?.length > 1">
      <div
        ref="availableColors"
        class="colors"
      >
        <div
          v-for="color in colorMap"
          :id="'swatch-'+color?.id"
          :key="color?.id"
          class="swatch-container"
          @click="selectedColor = color"
        >
          <Picture
            :image-url="urlTemplate + color?.swatchImage?.filePath"
            :alt="color?.name"
            :aspect-ratio="1"
            :image-intrinsic-width="36"
          />
        </div>
      </div>
    </div>
    <div v-if="sizeOptions?.length > 1">
      <div

        class="size-label"
      >
        <span class="field-name">SIZE:</span> {{ selectedSize?.text }}
      </div>
      <Form
        class="sizes"
      >
        <Dropdown
          v-if="useDropdownForSizes && sizeOptions?.length"
          name="sizes"
          label="Select a size"
          :options="sizeOptions.filter(option => !option?.unavailable)"
          @update:modelValue="(value) => selectASize(+value)"
        />
        <SelectionTile
          v-else
          v-model="preSelectedSize"
          name="sizes"
          :options="sizeOptions"
          cell-spacing="shrink"
          :size-tile="true"
          @update:modelValue="selectASize"
        />
      </Form>
    </div>
  </div>
</template>

<script>
import Picture from '@atomic-ui/picture';
import Form from '@atomic-ui/form';
import SelectionTile from '@atomic-ui/selectionTile';
import Dropdown from '@atomic-ui/dropdown';

export default {
  name: 'ColorAndSize',
  components: {
    Picture,
    Form,
    SelectionTile,
    Dropdown,
  },
  props: {
    traits: {
      type: Object,
      default: () => {},
    },
    urlTemplate: {
      type: String,
      default: '',
    },
    productUrl: {
      type: String,
      default: '',
    },
    clickAction: {
      type: String,
      default: 'linkToPdp',
    },
    useDropdownForSizes: {
      type: Boolean,
      default: false,
    },
    preSelectCombination: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  emits: ['setSelectedColor', 'setSelectedSize', 'setSelectedPrice', 'setUpcId', 'setSelectedSizeImages'],
  data() {
    return {
      selectedColorName: this.traits?.colors?.selectedColor?.name || '',
      availableSizes: this.traits?.colors?.selectedColor?.sizes || [],
      selectedColor: this.traits?.colors?.selectedColor,
      selectedSize: {},
      sizeOptions: [],
      preSelectedSize: null,
    };
  },
  computed: {
    isOutOfStock() {
      const upcMap = this.traits?.traitsMaps?.upcMap;
      return upcMap && Object.keys(upcMap).length === 0;
    },
    colorMap() {
      let colors = [];
      // if selected color is not first one in colorMap array then selected color is moved to first element.
      if (this.isOutOfStock) return colors;
      if (
        this.traits?.colors?.selectedColor
        !== Object.values(this.traits?.colors?.colorMap)[0]?.id
      ) {
        colors = Object.values(this.traits.colors?.colorMap).filter((color) => color.id !== this.traits?.colors?.selectedColor.id);
        if (typeof (this.traits?.colors?.selectedColor) === 'object') {
          colors.unshift(this.traits?.colors?.selectedColor);
        }
      }
      colors = colors.length === 0 ? this.traits?.colors?.colorMap : colors;
      return colors;
    },
    sizeMap() {
      const sizes = [];
      // return array of sizes from sizeMapObject.
      if (this.traits?.sizes?.sizeMap) {
        const sizesArray = this.traits.sizes?.sizeMap;
        // Updates size id in values with keys if there are not same
        return Object.keys(sizesArray).map((sizeKey) => {
          if (sizesArray[parseFloat(sizeKey)].id !== parseFloat(sizeKey)) {
            sizesArray[parseFloat(sizeKey)].id = parseFloat(sizeKey);
          }
          return sizesArray[parseFloat(sizeKey)];
        });
      }

      return sizes;
    },
  },
  watch: {
    selectedColor(newSelectedColor) {
      this.sizeOptions = this.traits.sizes ? Object.keys(this.traits.sizes?.sizeMap).map((item) => ({
        id: parseFloat(item),
        text: this.traits.sizes?.sizeMap[item].name,
        value: parseFloat(item),
        displayValue: this.traits.sizes?.sizeMap[item].name,
        unavailable: !this.traits.sizes?.sizeMap[item].colors.includes(newSelectedColor.id),
      })) : [];
      this.selectedColorName = newSelectedColor.name;
      this.availableSizes = newSelectedColor.sizes;
      if (this.traits?.colors?.colorMap.length > 1) {
        this.$refs.availableColors.querySelectorAll('div .swatch-container').forEach((element) => {
          if (String(newSelectedColor.id) !== element.id.slice(7)) {
            element.classList.remove('selected');
          } else {
            element.classList.add('selected');
          }
        });
      }
      this.emitUpcId(this.selectedColor.id, this.selectedSize.id);
      this.$emit('setSelectedColor', newSelectedColor.id);
      this.$emit('setSelectedPrice', Object.values(this.traits.colors.colorMap).find((color) => color.id === newSelectedColor.id).pricing.price);
    },
  },
  mounted() {
    this.sizeOptions = this.traits?.sizes
      ? Object.keys(this.traits?.sizes?.sizeMap)
        .map((item) => ({
          id: parseFloat(item),
          text: this.traits?.sizes?.sizeMap[item].name,
          value: parseFloat(item),
          displayValue: this.traits?.sizes?.sizeMap[item].name,
          unavailable: !this.availableSizes.includes(parseFloat(item)),
        })) : [];
    if (this.traits?.colors?.colorMap?.length > 1) {
      this.$refs.availableColors.querySelectorAll('div .swatch-container').forEach((element) => {
        if (String(this.selectedColor.id) !== element.id.slice(7)) {
          element.classList.remove('selected');
        } else {
          element.classList.add('selected');
        }
      });
    }
    if (this.preSelectCombination && Object.keys(this.preSelectCombination).length !== 0) {
      const preSelectedColor = this.preSelectCombination.upcSelectedColor;
      const preSelectedSize = this.preSelectCombination.upcSelectedSize;
      const newSelectedColor = Object.values(this.traits.colors.colorMap).find((color) => color.id === preSelectedColor);
      this.selectedColor = newSelectedColor;
      this.preSelectedSize = preSelectedSize;
      this.selectASize(preSelectedSize);
    } else if (this.sizeMap.length === 1) {
      this.selectedSize = {
        id: this.sizeMap[0].id,
        text: this.sizeMap[0].name,
        value: this.sizeMap[0].id,
        unavailable: !this.availableSizes.includes(this.sizeMap[0].id),
      };
    } else if (this.sizeMap.length > 1 && Number.isInteger(this.traits?.sizes?.selectedSize)) {
      const size = this.sizeMap.find((item) => item.id === this.traits?.sizes?.selectedSize);
      const sizeOpted = {
        id: size.id,
        text: size.name,
        value: size.name,
        displayValue: size.name,
        unavailable: !this.availableSizes.includes(size.id),
      };
      this.preSelectedSize = sizeOpted.id;
      this.selectASize(this.preSelectedSize);
    } else {
      this.selectedSize = {
        id: null,
        text: 'Please Select',
        value: null,
        displayValue: 'Please Select',
        unavailable: false,
      };
    }
    this.emitUpcId(this.selectedColor?.id, this.selectedSize?.id);
  },

  methods: {
    isInStock(id) {
      return this.availableSizes.includes(id) ? 'in-stock' : 'out-of-stock';
    },
    selectASize(sizeId) {
      if (typeof (sizeId) === 'number') {
        this.selectedSize = this.sizeOptions.find((item) => item.id === sizeId);
        this.$emit('setSelectedSize', this.selectedSize);
        const availableColors = Array.from(Object.values(this.sizeMap).find((size) => size.id === sizeId).colors).map((color) => String(color));
        if (this.traits?.colors?.colorMap?.length > 1) {
          this.$refs.availableColors.querySelectorAll('div .swatch-container').forEach((element) => {
            if (availableColors.includes(element.id.slice(7))) {
              element.classList.remove('out-of-stock');
            } else {
              element.classList.add('out-of-stock');
            }
          });
        }
        this.emitUpcId(this.selectedColor.id, this.selectedSize.id);
        const sizeDetails = this.sizeMap.find((size) => size.id === sizeId);
        if (sizeDetails?.pricing && sizeDetails?.pricing?.price) {
          this.$emit('setSelectedPrice', sizeDetails?.pricing?.price);
        }
        if (sizeDetails?.imagery && sizeDetails?.imagery?.images) {
          this.$emit('setSelectedSizeImages', sizeDetails?.imagery?.images);
        }
      } else {
        this.selectedSize = {
          id: null,
          text: 'Please Select',
          value: null,
          displayValue: 'Please Select',
          unavailable: false,
        };
      }
    },
    emitUpcId(colorId, sizeId) {
      const isColorOnlyEmit = typeof (colorId) === 'number' && this.traits.sizes === undefined;
      const isOtherEmit = typeof (colorId) === 'number' && typeof (sizeId) === 'number';
      if (isColorOnlyEmit || isOtherEmit) {
        const upcIdKey = isColorOnlyEmit ? String(colorId) : `${String(colorId)}_${String(sizeId)}`;
        const upcIdInList = this.traits?.traitsMaps?.upcMap[upcIdKey];
        this.$emit('setUpcId', {
          upcId: upcIdInList === undefined ? undefined : upcIdInList,
          availability: upcIdInList !== undefined,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~settingSCSS";
.color-and-size-container {
  margin-top: rem-calc(7.5);
  margin-bottom: rem-calc(8.5);
  overflow: hidden;
  .color-name-label, .size-label {
    font-size: $font-size-med;
    line-height: $line-height-med;
    font-weight: $font-weight-med;
    margin: rem-calc(32 0 32 0);
    clear: both;
  }
  .color-name-label {
    text-transform: uppercase;
  }
  .colors {
    :deep(.picture-image){
      width: rem-calc(34);
      height: rem-calc(34);
      margin: rem-calc(1);
      display: block;
      cursor: pointer;
    }
    .swatch-container {
      width: rem-calc(42);
      height: rem-calc(42);
      margin: rem-calc(0 13 13 0);
      float: left;
      border-radius:$radio-border-radius;
      border: solid 3px #fff;
      -webkit-box-shadow:inset 0px 0px 0px 1px #959499;
      -moz-box-shadow:inset 0px 0px 0px 1px #959499;
      box-shadow:inset 0px 0px 0px 1px #959499;
    }
    .swatch-container.out-of-stock {
      :deep(.picture-container::before) {
        position: absolute;
        transform: rotate(45deg);
        border-bottom: $gray-2-border;
        content: "";
        background-color: $input-selected-color;
        margin-top:rem-calc(16);
      }
      :deep(.picture-container::after) {
        position: absolute;
        transform: rotate(45deg);
        border-bottom: $radio-inner-border;
        content: "";
        display: inline-block;
        margin-top: rem-calc(-18);
      }
    }
    div.selected {
      :deep(.picture-image){
        width: rem-calc(36);
        height: rem-calc(36);
        margin: 0;
      }
      border: $black-border;
      padding: rem-calc(2);
      box-shadow:inset 0px 0px 0px 1px #fff;
    }
    :deep(.picture-image).unavailable {
      opacity: .1;
    }
  }
  .sizes {
    :deep(.cell) {
      margin-bottom: rem-calc(16);
    }
  }
}
</style>
<style lang="scss" brand="mcom" scoped>
.color-and-size-container {
  .colors {
    :deep(.picture-image){
      border-radius: $radio-border-radius;
    }
    .swatch-container {
      border-radius: $radio-border-radius;
    }
    .swatch-container.out-of-stock {
      :deep(.picture-container::before) {
        width: 2.125rem;
      }
      :deep(.picture-container::after) {
        width: 2.125rem;
      }
    }
  }
}
</style>
<style lang="scss" brand="bcom" scoped>
.color-and-size-container {
  .colors {
    :deep(.picture-image){
      border-radius:0;
    }
    .swatch-container {
      border-radius:0;
    }
    .swatch-container.out-of-stock {
      :deep(.picture-container::before) {
        width: 2.7rem;
        margin-left: rem-calc(-3);
      }
      :deep(.picture-container::after) {
        width: 2.7rem;
        margin-left: rem-calc(-3);
      }
    }
  }
}
</style>
